import { GAMMA_MEDIUM_CHAIN } from "./gamma";
import { windSeaAlternative } from "./rideable-wave-partitions";
import { summariseDailyQuality } from "./hourly-windows";
import type { SunTimes } from "./daylight";
import { isDark } from "./daylight";
import { composeScore, defineChain } from "./compose-score";
import { REVISION_ONE_FORMULA, isRevisionOneFormula, type LeafFormula } from "./engines/core/formula";
import {
  directionAverage,
  partitionsFromLeafReading,
  riddenTrains,
  type RiddenTrain,
  type WavePartition,
} from "./swell-partitions";
import { tideMultiplierFromPosition } from "./tide-legacy";

// Bodyboarding's chain, declared as data (tracker P3-19); classes are enforced
// by composeScore. Gamma derived from the 10 declared factors (MEDIUM) under
// the Phase 6 all-or-none policy (tracker P6-1, audit 02 V4 / 90.3).
export const BODYBOARD_CHAIN = defineChain({
  sport: "bodyboarding",
  gamma: GAMMA_MEDIUM_CHAIN, // Pinned calibration; factor-list refactors must not change the scale.
  factors: [
    { key: "waveHeightBase", class: "driver" },
    { key: "steepness", class: "driver", note: "Wave organisation from period; local breaking shape cannot be inferred from H/T." },
    { key: "direction", class: "driver" },
    { key: "chop", class: "driver" },
    { key: "wind", class: "driver" },
    { key: "gust", class: "driver", note: "wind-quality family" },
    { key: "tide", class: "driver" },
    { key: "current", class: "driver", note: "quality + flag from one input is the sanctioned pattern (02 B2)" },
    { key: "rain", class: "modifier", note: "floor 0.85: the fleet's compliant reference modifier (P7-3)" },
    { key: "visibility", class: "modifier", note: "P7-4 (audit 02 V1): sea mist does not change wave shape/power for a shorebreak bodyboarder, so fog floors as an enjoyment modifier and the new bodyboardSafetyIndicators fog flag (P7-5) carries the hazard. Floor is internal to composeScore; visibilityFactor surfaces the raw curve value" },
  ],
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * Math.max(0, Math.min(1, t));
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function sigmoid(x: number, midpoint: number, steepness: number): number {
  return 1 / (1 + Math.exp((x - midpoint) / steepness));
}

function angleDiff(a: number, b: number): number {
  let d = Math.abs(a - b);
  if (d > 180) d = 360 - d;
  return d;
}

// ---------------------------------------------------------------------------
// Constants -- Wave height
// ---------------------------------------------------------------------------

const MINIMUM_RIDEABLE_SWELL_M = 0.15;

// ---------------------------------------------------------------------------
// Constants -- Direction
// ---------------------------------------------------------------------------

const DIR_SIGMA: Record<string, number> = {
  beach: 65,
  reef: 50,
  point: 75,
  rivermouth: 65,
};
const DIR_DEFAULT_SIGMA = 65;
const DIR_FLOOR = 0.15;
// Unknown swell direction or unconfigured beach facing: neutral, never a
// penalty. Data completeness must not decide which spots can score 9+.
const DIR_UNKNOWN_DEFAULT = 1.0;

// ---------------------------------------------------------------------------
// Constants -- Wind
// ---------------------------------------------------------------------------

const WIND_OFF_FLOOR = 0.10;
const WIND_OFF_RANGE = 0.90;
const WIND_OFF_MIDPOINT = 40;
const WIND_OFF_STEEPNESS = 7;

const WIND_ON_FLOOR = 0.08;
const WIND_ON_RANGE = 0.92;
const WIND_ON_MIDPOINT = 25;
const WIND_ON_STEEPNESS = 5;

// No beach facing configured: blended curve between the onshore and offshore
// responses. Range gives a ceiling of 1.0 so calm days at unconfigured spots
// are not taxed for missing config.
const WIND_UNKNOWN_FLOOR = 0.10;
const WIND_UNKNOWN_RANGE = 0.90;
const WIND_UNKNOWN_MIDPOINT = 30;
const WIND_UNKNOWN_STEEPNESS = 6;

// ---------------------------------------------------------------------------
// Constants -- Gust
// ---------------------------------------------------------------------------

const GUST_TRIGGER_KMH = 18;
const GUST_WIND_GATE_KMH = 5;
const GUST_SAFE_RATIO = 1.3;
const GUST_PENALTY_SCALE = 0.4;
const GUST_FLOOR = 0.70;

// ---------------------------------------------------------------------------
// Constants -- Tide
// ---------------------------------------------------------------------------

const TIDE_SENSITIVITY: Record<string, number> = {
  steep: 1.3,
  moderate: 1.0,
  gentle: 0.5,
};
const TIDE_FLOOR = 0.50;
// No tide data or unknown beach gradient: neutral. A penalising default was
// a permanent multiplicative tax on every spot without real tide data.
const TIDE_DEFAULT = 1.0;

// ---------------------------------------------------------------------------
// Constants -- Rain
// ---------------------------------------------------------------------------

const RAIN_FLOOR = 0.85;
const RAIN_HALF_PRECIP = 25;

// ---------------------------------------------------------------------------
// Constants -- Visibility
// ---------------------------------------------------------------------------

const VIS_FLOOR = 0.30;
const VIS_RANGE = 0.70;
const VIS_MIDPOINT = 600;
const VIS_STEEPNESS = 350;

// ---------------------------------------------------------------------------
// Constants -- Daily
// ---------------------------------------------------------------------------

const DAILY_BEST_WINDOW_WEIGHT = 0.70;
const DAILY_BEST_WINDOW_HOURS = 3;

// ---------------------------------------------------------------------------
// Factor 1: Wave height base (0--100)
// ---------------------------------------------------------------------------

export function bodyboardWaveHeightBase(h: number): number {
  if (h < 0.15) return 0;
  if (h < 0.30) return lerp(0, 15, (h - 0.15) / 0.15);
  if (h < 0.50) return lerp(15, 40, (h - 0.30) / 0.20);
  if (h < 0.70) return lerp(40, 60, (h - 0.50) / 0.20);
  if (h < 0.90) return lerp(60, 78, (h - 0.70) / 0.20);
  if (h < 1.20) return lerp(78, 92, (h - 0.90) / 0.30);
  if (h < 1.80) return lerp(92, 100, (h - 1.20) / 0.60);
  if (h < 2.50) return lerp(100, 78, (h - 1.80) / 0.70);
  if (h < 3.50) return lerp(78, 40, (h - 2.50) / 1.00);
  if (h < 4.50) return lerp(40, 18, (h - 3.50) / 1.00);
  return 18;
}

// ---------------------------------------------------------------------------
// Factor 2: Period organisation (0.65-1.00)
//
// Period describes offshore organisation, not the hollow shape at the break.
// ---------------------------------------------------------------------------

/** Compatibility name for stored factor keys. Offshore H/T is not the
 * hollowness of a breaking wave. Period describes organisation here; local
 * bathymetry and tide remain separate rather than rewarding every short sea. */
export function bodyboardSteepnessFactor(_heightM: number, periodS: number): number {
  if (periodS <= 4) return 0.65;
  if (periodS <= 6) return lerp(0.65, 0.85, (periodS - 4) / 2);
  if (periodS <= 8) return lerp(0.85, 0.96, (periodS - 6) / 2);
  if (periodS <= 10) return lerp(0.96, 1, (periodS - 8) / 2);
  return 1;
}

// ---------------------------------------------------------------------------
// Factor 3: Direction alignment (0.15--1.0)
// ---------------------------------------------------------------------------

export function bodyboardDirectionFactor(
  swellDir: number | null,
  beachFacing: number | null,
  breakType?: string | null,
): number {
  if (beachFacing === null || swellDir === null) return DIR_UNKNOWN_DEFAULT;
  const delta = angleDiff(swellDir, beachFacing);
  const s = DIR_SIGMA[breakType ?? ""] ?? DIR_DEFAULT_SIGMA;
  return Math.max(DIR_FLOOR, Math.exp(-0.5 * (delta / s) ** 2));
}

// ---------------------------------------------------------------------------
// Factor 4: Chop contamination (0.50--1.0)
// ---------------------------------------------------------------------------

export function bodyboardChopFactor(
  effectiveWindWaveH: number,
  effectiveSwellH: number,
): number {
  if (effectiveSwellH <= 0) return 1.0;
  const ratio = effectiveWindWaveH / effectiveSwellH;
  if (ratio < 0.10) return 1.0;
  const basePenalty = Math.min(0.45, 0.45 * ratio ** 2);
  return Math.max(0.50, 1.0 - basePenalty);
}

// ---------------------------------------------------------------------------
// Factor 5: Wind quality (0.10--0.95)
// ---------------------------------------------------------------------------

export function bodyboardWindQuality(
  speed: number,
  windDir: number | null,
  beachFacing: number | null,
): number {
  if (beachFacing === null || windDir === null) {
    return WIND_UNKNOWN_FLOOR + WIND_UNKNOWN_RANGE * sigmoid(speed, WIND_UNKNOWN_MIDPOINT, WIND_UNKNOWN_STEEPNESS);
  }

  const offshoreIdeal = (beachFacing + 180) % 360;
  const windAngle = angleDiff(windDir, offshoreIdeal);

  const fOff = WIND_OFF_FLOOR + WIND_OFF_RANGE * sigmoid(speed, WIND_OFF_MIDPOINT, WIND_OFF_STEEPNESS);
  const fOn = WIND_ON_FLOOR + WIND_ON_RANGE * sigmoid(speed, WIND_ON_MIDPOINT, WIND_ON_STEEPNESS);

  const t = (1 - Math.cos((windAngle * Math.PI) / 180)) / 2;
  return fOff * (1 - t) + fOn * t;
}

// ---------------------------------------------------------------------------
// Factor 6: Gust penalty (0.70--1.0)
// ---------------------------------------------------------------------------

export function bodyboardGustPenalty(
  sustained: number,
  gust: number | null | undefined,
): number {
  if (gust == null || gust < GUST_TRIGGER_KMH || sustained < GUST_WIND_GATE_KMH) return 1.0;
  const excess = Math.max(0, gust / sustained - GUST_SAFE_RATIO);
  return Math.max(GUST_FLOOR, 1.0 - GUST_PENALTY_SCALE * excess ** 2);
}

// ---------------------------------------------------------------------------
// Factor 7: Tide (0.50--1.0)
// ---------------------------------------------------------------------------

function baseTideFactor(pos: number): number {
  if (pos < 0.10) return 0.65;
  if (pos < 0.25) return lerp(0.65, 0.85, (pos - 0.10) / 0.15);
  if (pos < 0.40) return lerp(0.85, 1.00, (pos - 0.25) / 0.15);
  if (pos < 0.55) return 1.00;
  if (pos < 0.75) return lerp(1.00, 0.82, (pos - 0.55) / 0.20);
  if (pos < 0.90) return lerp(0.82, 0.60, (pos - 0.75) / 0.15);
  return 0.60;
}

export function bodyboardTideFactor(
  tidePosition: number | null,
  beachGradient?: string | null,
): number {
  if (tidePosition === null || beachGradient == null) return TIDE_DEFAULT;
  const sensitivity = TIDE_SENSITIVITY[beachGradient] ?? 1.0;
  return Math.max(TIDE_FLOOR, 1.0 - sensitivity * (1.0 - baseTideFactor(tidePosition)));
}

// ---------------------------------------------------------------------------
// Factor 8: Current (0.65--1.0)
// ---------------------------------------------------------------------------

export function bodyboardCurrentFactor(velocity: number | null): number {
  if (velocity === null) return 1.0;
  if (velocity <= 0.4) return 1.0;
  if (velocity <= 0.8) return lerp(1.0, 0.90, (velocity - 0.4) / 0.4);
  if (velocity <= 1.2) return lerp(0.90, 0.75, (velocity - 0.8) / 0.4);
  if (velocity <= 1.6) return lerp(0.75, 0.65, (velocity - 1.2) / 0.4);
  return 0.65;
}

// ---------------------------------------------------------------------------
// Factor 9: Rain (0.85--1.0)
// ---------------------------------------------------------------------------

export function bodyboardRainFactor(precipMm: number): number {
  return RAIN_FLOOR + (1 - RAIN_FLOOR) / (1 + (precipMm / RAIN_HALF_PRECIP) ** 3);
}

// ---------------------------------------------------------------------------
// Factor 10: Visibility (0.30--1.0)
// ---------------------------------------------------------------------------

export function bodyboardVisibilityFactor(visM: number | null | undefined): number {
  if (visM == null) return 1.0;
  return VIS_FLOOR + VIS_RANGE / (1 + Math.exp(-(visM - VIS_MIDPOINT) / VIS_STEEPNESS));
}

// ---------------------------------------------------------------------------
// Legacy beach-profile multiplier.
//
// P7-11 removes the static steep/moderate/gentle venue rating from the score:
// it never varies across a decision window. Beach gradient still matters where
// it is conditional, inside bodyboardTideFactor().
// ---------------------------------------------------------------------------

export function bodyboardBeachProfileMultiplier(
  _gradient?: string | null,
): number {
  return 1.00;
}

// ---------------------------------------------------------------------------
// Safety indicators
// ---------------------------------------------------------------------------

export type BodyboardSafetyLevel = "green" | "amber" | "red";

export type BodyboardSafetyIndicator = {
  severity: BodyboardSafetyLevel;
  label: string;
  detail?: string;
};

export function bodyboardSafetyIndicators(input: {
  waveHeight: number;
  windSpeed: number;
  currentVelocity: number | null;
  tidePosition: number | null;
  beachGradient?: string | null;
  weatherCode: number;
  visibilityM?: number | null;
}): BodyboardSafetyIndicator[] {
  const indicators: BodyboardSafetyIndicator[] = [];

  if ([95, 96, 99].includes(input.weatherCode)) {
    indicators.push({
      severity: "red",
      label: "Thunderstorm",
      detail: "Thunderstorm detected. Do not enter the water.",
    });
  }

  // Fog flag (P7-5, audit 02 V1): paired with the P7-4 reclassification of
  // visibility to a floored modifier. Sea mist leaves wave shape unchanged, so
  // fog no longer tanks the score; the hazard is carried here. Matches the
  // surf/foil/downwind/wing/SUP/swim siblings: red < 500m, amber < 1000m.
  const visM = input.visibilityM;
  if (visM != null && visM < 500) {
    indicators.push({
      severity: "red",
      label: "Dense fog",
      detail: `Visibility ${Math.round(visM)}m. Cannot see boats, the shore, or other riders.`,
    });
  } else if (visM != null && visM < 1000) {
    indicators.push({
      severity: "amber",
      label: "Reduced visibility",
      detail: `Visibility ${Math.round(visM)}m. Keep a clear reference to the shore.`,
    });
  }

  if (input.waveHeight > 4.0) {
    indicators.push({
      severity: "red",
      label: "Very large swell",
      detail: `Swell ${input.waveHeight.toFixed(1)}m. Expert bodyboarders only.`,
    });
  } else if (input.waveHeight > 2.5) {
    indicators.push({
      severity: "amber",
      label: "Large swell",
      detail: `Swell ${input.waveHeight.toFixed(1)}m. Experienced bodyboarders only.`,
    });
  }

  if (input.currentVelocity !== null && input.currentVelocity > 1.5) {
    indicators.push({
      severity: "red",
      label: "Very strong current",
      detail: `Current ${input.currentVelocity.toFixed(1)} m/s. High risk of being swept out.`,
    });
  } else if (input.currentVelocity !== null && input.currentVelocity > 0.8) {
    indicators.push({
      severity: "amber",
      label: "Strong current",
      detail: `Current ${input.currentVelocity.toFixed(1)} m/s. Assess drift risk before entering.`,
    });
  }

  if (input.windSpeed > 45) {
    indicators.push({
      severity: "red",
      label: "Gale wind",
      detail: `Wind ${Math.round(input.windSpeed)} km/h. Dangerous conditions.`,
    });
  }

  if (
    input.tidePosition !== null &&
    input.tidePosition < 0.10 &&
    input.beachGradient === "steep"
  ) {
    indicators.push({
      severity: "amber",
      label: "Shallow water",
      detail: "Very low tide on steep beach. Risk of impact with seabed.",
    });
  }

  return indicators;
}

// ---------------------------------------------------------------------------
// Input / output types
// ---------------------------------------------------------------------------

export interface BodyboardMarineInput {
  swellWaveHeightM: number;
  swellWavePeriodS: number;
  swellWaveDirectionDeg: number | null;
  windWaveHeightM: number;
  windWavePeriodS?: number | null;
  windWaveDirectionDeg?: number | null;
  /** Revision 2 only (revision 1 has no secondary swell and ignores these): the other swell partitions. */
  secondarySwellWaveHeightM?: number | null;
  secondarySwellWavePeriodS?: number | null;
  secondarySwellWaveDirectionDeg?: number | null;
  tertiarySwellWaveHeightM?: number | null;
  tertiarySwellWavePeriodS?: number | null;
  tertiarySwellWaveDirectionDeg?: number | null;
  /**
   * Revision 2 only (revision 1 ignores it): the hour's complete wave partitions, each height
   * already scaled by the shelter of its own direction (swell-partitions.ts). When given, the
   * revision 2 formula scores from these; when absent it builds them from the fields above with
   * `config.shelterFactor`.
   */
  partitions?: readonly WavePartition[];
}

export interface BodyboardWeatherInput {
  windSpeedKmh: number;
  windDirectionDeg: number | null;
  windGustsKmh?: number;
  precipitationMm: number;
  visibilityM?: number | null;
}

export interface BodyboardSpotConfig {
  beachFacingDeg: number | null;
  breakType?: string | null;
  shelterFactor?: number;
  beachGradient?: "steep" | "moderate" | "gentle" | null;
  /** Revision 2 `tide: "preference"` only (BB-1): the spot's tide preference, shared with surf. */
  tidePreference?: string | null;
}

export interface BodyboardHourlyResult {
  waveSource?: "swell" | "wind-sea";
  score: number;
  waveHeightBase: number;
  steepnessFactor: number;
  directionFactor: number;
  chopFactor: number;
  windFactor: number;
  gustFactor: number;
  tideFactor: number;
  currentFactor: number;
  rainFactor: number;
  visibilityFactor: number;
  beachProfileMultiplier: number;
  readings: Readonly<{
    effectiveSwellHeightM: number;
    effectiveWindWaveHeightM: number;
  }>;
}

// ---------------------------------------------------------------------------
// Hourly scoring
// ---------------------------------------------------------------------------

/**
 * The trailing formula argument (engines/core/formula.ts). Omitted or revision 1: today's scorer,
 * bit for bit (engines/core/__tests__/leaf-revision-one.test.ts). Revision 2 and later, used only
 * by the bodyboarding revision 2 kernel (engines/coastal/r2/bodyboarding.ts, work package WP-S1):
 * - every complete partition is a candidate ridden train, shared with surf (SURF-2,
 *   swell-partitions.ts), the other partitions charged as interference by the chop curve;
 * - `chop: "directional"`: interference weighted by how much of each sea reaches the break;
 * - `gust: "absolute"`: absolute gust behaviour (SURF-3, BODYBOARD_ABSOLUTE_GUST);
 * - `tide: "preference"`: the tide response comes from the spot's tide preference, scaled by
 *   the beach gradient and neutral for "all tides" (BB-1, bodyboardPreferenceTideFactor);
 * - a missing swell or wind direction at a configured facing takes the direction-averaged
 *   factor, a missing gust the sustained speed, a missing visibility or current is neutral
 *   (design 2.5);
 * - the hourly score keeps its native precision (carriers round).
 */
export type BodyboardFormula = LeafFormula<{
  chop: "flat" | "directional";
  gust: "ratio" | "absolute";
  tide: "gradient" | "preference";
}>;

export function calculateBodyboardHourly(
  marine: BodyboardMarineInput,
  weather: BodyboardWeatherInput,
  config: BodyboardSpotConfig,
  tidePosition: number | null,
  currentVelocityMs: number | null,
  formula: BodyboardFormula = REVISION_ONE_FORMULA,
): BodyboardHourlyResult {
  if (!isRevisionOneFormula(formula)) {
    return calculateBodyboardRevisionTwoHourly(marine, weather, config, tidePosition, currentVelocityMs, formula);
  }
  const swell = scoreBodyboardPartition(marine, weather, config, tidePosition, currentVelocityMs);
  const alternative = windSeaAlternative(marine);
  if (alternative === null) return { ...swell, waveSource: "swell" };
  const wind = scoreBodyboardPartition(alternative, weather, config, tidePosition, currentVelocityMs);
  return wind.score > swell.score ? { ...wind, waveSource: "wind-sea" } : { ...swell, waveSource: "swell" };
}

function scoreBodyboardPartition(
  marine: BodyboardMarineInput,
  weather: BodyboardWeatherInput,
  config: BodyboardSpotConfig,
  tidePosition: number | null,
  currentVelocityMs: number | null,
): BodyboardHourlyResult {
  const shelter = config.shelterFactor ?? 1.0;
  const effectiveSwellH = marine.swellWaveHeightM * shelter;
  const effectiveWindWaveH = marine.windWaveHeightM * shelter;

  if (effectiveSwellH < MINIMUM_RIDEABLE_SWELL_M) {
    return {
      score: 0,
      waveHeightBase: 0,
      steepnessFactor: 1,
      directionFactor: 1,
      chopFactor: 1,
      windFactor: 1,
      gustFactor: 1,
      tideFactor: 1,
      currentFactor: 1,
      rainFactor: 1,
      visibilityFactor: 1,
      beachProfileMultiplier: 1,
      readings: Object.freeze({ effectiveSwellHeightM: effectiveSwellH, effectiveWindWaveHeightM: effectiveWindWaveH }),
    };
  }

  const whBase = bodyboardWaveHeightBase(effectiveSwellH);
  const steep = bodyboardSteepnessFactor(effectiveSwellH, marine.swellWavePeriodS);
  const dir = bodyboardDirectionFactor(marine.swellWaveDirectionDeg, config.beachFacingDeg, config.breakType);
  const chop = bodyboardChopFactor(effectiveWindWaveH, effectiveSwellH);
  const wind = bodyboardWindQuality(weather.windSpeedKmh, weather.windDirectionDeg, config.beachFacingDeg);
  const gust = bodyboardGustPenalty(weather.windSpeedKmh, weather.windGustsKmh);
  const tide = bodyboardTideFactor(tidePosition, config.beachGradient);
  const current = bodyboardCurrentFactor(currentVelocityMs);
  const rain = bodyboardRainFactor(weather.precipitationMm);
  const vis = bodyboardVisibilityFactor(weather.visibilityM);
  const profile = bodyboardBeachProfileMultiplier(config.beachGradient);

  // whBase is 0-100; normalise to 0-1 so gamma compresses in its intended
  // domain, then scale back by 100 (P6-1, matching the surf/wind/foil chains).
  const raw = 100 * composeScore(BODYBOARD_CHAIN, {
    waveHeightBase: whBase / 100,
    steepness: steep,
    direction: dir,
    chop,
    wind,
    gust,
    tide,
    current,
    rain,
    visibility: vis,
  }).compressed;
  const score = clamp(Math.round(raw), 0, 100);

  return {
    score,
    waveHeightBase: whBase,
    steepnessFactor: steep,
    directionFactor: dir,
    chopFactor: chop,
    windFactor: wind,
    gustFactor: gust,
    tideFactor: tide,
    currentFactor: current,
    rainFactor: rain,
    visibilityFactor: vis,
    beachProfileMultiplier: profile,
    readings: Object.freeze({ effectiveSwellHeightM: effectiveSwellH, effectiveWindWaveHeightM: effectiveWindWaveH }),
  };
}

// ---------------------------------------------------------------------------
// Revision 2 (trailing formula argument)
// ---------------------------------------------------------------------------

/**
 * SURF-3 for bodyboarding (revision 2, `gust: "absolute"`): the surf activation points (no penalty
 * unless gusts are at least 25 km/h and the sustained wind at least 12 km/h, design 6.3, a step as
 * for surf), a wider spread (bodyboarding tolerates gusts better, as its revision 1 curve does) and
 * revision 1's floor. Monotonic in gusts; a gust equal to the sustained speed is exactly 1. The
 * same arithmetic as surf-scoring-v5.ts absoluteGustPenalty, kept here because a value import of
 * another scoring module would be a new scorer importer (engines/core/__tests__/scorer-importers.test.ts).
 */
export const BODYBOARD_ABSOLUTE_GUST = Object.freeze({
  minGustKmh: 25,
  minWindKmh: 12,
  spreadKmh: 24,
  floor: GUST_FLOOR,
});

/** The bodyboarding SURF-3 gust factor (see BODYBOARD_ABSOLUTE_GUST). */
export function bodyboardAbsoluteGustPenalty(sustained: number, gust: number): number {
  const params = BODYBOARD_ABSOLUTE_GUST;
  const excess = gust - sustained;
  if (!(excess > 0) || !(gust >= params.minGustKmh) || !(sustained >= params.minWindKmh)) return 1;
  return 1 - (1 - params.floor) * Math.min(1, (excess / params.spreadKmh) ** 2);
}

/**
 * BB-1 (revision 2, `tide: "preference"`): the tide response comes from the spot's tide
 * preference, through the same weights as surf (tide-legacy.ts tideMultiplierFromPosition), and
 * the beach gradient scales it (steep 1.3, moderate 1.0, gentle 0.5; an unknown gradient takes
 * the moderate central estimate). "All tides", an unknown preference or no tide position are
 * neutral. The position is the day-range position of the harmonic tide (review SURF-1).
 */
export function bodyboardPreferenceTideFactor(
  tidePosition: number | null,
  tidePreference: string | null | undefined,
  beachGradient?: string | null,
): number {
  if (tidePosition === null) return TIDE_DEFAULT;
  const multiplier = tideMultiplierFromPosition(tidePosition, tidePreference ?? null) ?? TIDE_DEFAULT;
  const sensitivity = TIDE_SENSITIVITY[beachGradient ?? ""] ?? TIDE_SENSITIVITY.moderate;
  return Math.max(TIDE_FLOOR, 1.0 - sensitivity * (1.0 - multiplier));
}

function directionFactorRevisionTwo(swellDir: number | null, beachFacing: number | null, breakType?: string | null): number {
  if (beachFacing === null) return DIR_UNKNOWN_DEFAULT;
  if (swellDir === null) return directionAverage((direction) => bodyboardDirectionFactor(direction, beachFacing, breakType));
  return bodyboardDirectionFactor(swellDir, beachFacing, breakType);
}

function windQualityRevisionTwo(speed: number, windDir: number | null, beachFacing: number | null): number {
  if (beachFacing === null) return bodyboardWindQuality(speed, null, null);
  if (windDir === null) return directionAverage((direction) => bodyboardWindQuality(speed, direction, beachFacing));
  return bodyboardWindQuality(speed, windDir, beachFacing);
}

function flatBodyboardResult(heightM: number, interferenceM: number): BodyboardHourlyResult {
  return {
    score: 0,
    waveHeightBase: 0,
    steepnessFactor: 1,
    directionFactor: 1,
    chopFactor: 1,
    windFactor: 1,
    gustFactor: 1,
    tideFactor: 1,
    currentFactor: 1,
    rainFactor: 1,
    visibilityFactor: 1,
    beachProfileMultiplier: 1,
    readings: Object.freeze({ effectiveSwellHeightM: heightM, effectiveWindWaveHeightM: interferenceM }),
  };
}

function scoreBodyboardTrain(
  train: RiddenTrain,
  weather: BodyboardWeatherInput,
  config: BodyboardSpotConfig,
  tidePosition: number | null,
  currentVelocityMs: number | null,
  formula: BodyboardFormula,
): BodyboardHourlyResult {
  const height = train.heightM;
  if (height < MINIMUM_RIDEABLE_SWELL_M) return flatBodyboardResult(height, train.interferenceHeightM);

  const whBase = bodyboardWaveHeightBase(height);
  const steep = bodyboardSteepnessFactor(height, train.periodS);
  const dir = directionFactorRevisionTwo(train.directionDeg, config.beachFacingDeg, config.breakType);
  const chop = bodyboardChopFactor(train.interferenceHeightM, height);
  const wind = windQualityRevisionTwo(weather.windSpeedKmh, weather.windDirectionDeg, config.beachFacingDeg);
  const gust = formula.gust === "absolute"
    ? bodyboardAbsoluteGustPenalty(weather.windSpeedKmh, weather.windGustsKmh ?? weather.windSpeedKmh)
    : bodyboardGustPenalty(weather.windSpeedKmh, weather.windGustsKmh);
  const tide = formula.tide === "preference"
    ? bodyboardPreferenceTideFactor(tidePosition, config.tidePreference, config.beachGradient)
    : bodyboardTideFactor(tidePosition, config.beachGradient);
  const current = bodyboardCurrentFactor(currentVelocityMs);
  const rain = bodyboardRainFactor(weather.precipitationMm);
  const vis = bodyboardVisibilityFactor(weather.visibilityM);
  const profile = bodyboardBeachProfileMultiplier(config.beachGradient);

  const raw = 100 * composeScore(BODYBOARD_CHAIN, {
    waveHeightBase: whBase / 100,
    steepness: steep,
    direction: dir,
    chop,
    wind,
    gust,
    tide,
    current,
    rain,
    visibility: vis,
  }).compressed;

  return {
    // Native precision: the engine's carriers round (design 2.3).
    score: clamp(raw, 0, 100),
    waveHeightBase: whBase,
    steepnessFactor: steep,
    directionFactor: dir,
    chopFactor: chop,
    windFactor: wind,
    gustFactor: gust,
    tideFactor: tide,
    currentFactor: current,
    rainFactor: rain,
    visibilityFactor: vis,
    beachProfileMultiplier: profile,
    readings: Object.freeze({ effectiveSwellHeightM: height, effectiveWindWaveHeightM: train.interferenceHeightM }),
  };
}

/** Revision 2 and later: the best candidate ridden train of the hour (SURF-2, shared with surf). */
function calculateBodyboardRevisionTwoHourly(
  marine: BodyboardMarineInput,
  weather: BodyboardWeatherInput,
  config: BodyboardSpotConfig,
  tidePosition: number | null,
  currentVelocityMs: number | null,
  formula: BodyboardFormula,
): BodyboardHourlyResult {
  const partitions = marine.partitions ?? partitionsFromLeafReading(marine, config.shelterFactor ?? 1.0);
  const trains = riddenTrains(partitions, {
    beachFacingDeg: config.beachFacingDeg,
    directionalInterference: formula.chop === "directional",
  });
  let best: BodyboardHourlyResult | null = null;
  for (const train of trains) {
    const result = scoreBodyboardTrain(train, weather, config, tidePosition, currentVelocityMs, formula);
    if (best === null || result.score > best.score) best = result;
  }
  return best ?? flatBodyboardResult(0, 0);
}

// ---------------------------------------------------------------------------
// Daily derivation: 30% daylight average + 70% best 3h window
// ---------------------------------------------------------------------------

export function deriveBodyboardDaily(
  hourlyScores: { hour: number; score: number; isDay: boolean }[],
): { overall: number; bestWindowStart: number | null; bestWindowScores: number[] } {
  const daily = summariseDailyQuality(
    hourlyScores.map((point) => ({ ...point, isDay: point.isDay })),
    DAILY_BEST_WINDOW_HOURS, DAILY_BEST_WINDOW_WEIGHT,
  );
  return {
    overall: Math.round((daily.overall / 10) * 10) / 10,
    bestWindowStart: daily.bestWindowStart,
    bestWindowScores: daily.bestWindowScores,
  };
}

// ---------------------------------------------------------------------------
// Labels (applied to 0--10 daily score)
// ---------------------------------------------------------------------------

export function bodyboardLabel(score: number): string {
  if (score >= 9.0) return "Hero";
  if (score >= 7.5) return "Firing";
  if (score >= 6.5) return "Great";
  if (score >= 5.0) return "Good";
  if (score >= 3.5) return "Fair";
  if (score >= 2.0) return "Poor";
  return "Flat";
}

// ---------------------------------------------------------------------------
// Re-exports
// ---------------------------------------------------------------------------

export { isDark };
export type { SunTimes };
